import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { cn } from "@/lib/utils";
import { useDialogLoadingGuard, getMaxWidthClass } from "./dialog-utils";
import type { MaxWidth } from "./dialog-utils";

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (e: React.FormEvent) => void | Promise<void>;
  loading?: boolean;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  showCloseButton?: boolean;
  bodyClassName?: string;
  contentClassName?: string;
  maxWidth?: Exclude<MaxWidth, "xl">;
  submitDisabled?: boolean;
  submitLabel?: string;
  /** `destructive` para el guardado que borra algo. Por defecto, el primario. */
  submitVariant?: "default" | "destructive";
  submitLoadingText?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  preventAutoFocus?: boolean;
}

export const FormDialog = ({
  open,
  onOpenChange,
  onSubmit,
  loading = false,
  title,
  subtitle,
  children,
  showCloseButton = true,
  bodyClassName = "p-4 sm:p-6 space-y-4 sm:space-y-5",
  contentClassName,
  maxWidth = "md",
  submitDisabled = false,
  submitLabel = "Guardar",
  submitVariant = "default",
  submitLoadingText = "Guardando...",
  cancelLabel = "Cancelar",
  onCancel,
  preventAutoFocus = false,
}: FormDialogProps) => {
  const { handleOpenChange } = useDialogLoadingGuard(onOpenChange, loading);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={showCloseButton}
        className={cn(getMaxWidthClass(maxWidth), contentClassName)}
        {...(preventAutoFocus && { initialFocus: false })}
      >
        <form
          onSubmit={onSubmit}
          className="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {subtitle && (
              <DialogDescription>
                <span>{subtitle}</span>
              </DialogDescription>
            )}
          </DialogHeader>

          <div
            className={cn(
              "flex-1 min-h-0 overflow-y-auto",
              bodyClassName,
            )}
          >
            {children}
          </div>

          <DialogFooter>
            {cancelLabel && (
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                disabled={loading}
                onClick={() => (onCancel ? onCancel() : onOpenChange(false))}
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              type="submit"
              variant={submitVariant}
              className="flex-1"
              disabled={submitDisabled || loading}
              isLoading={loading}
            >
              {loading ? submitLoadingText : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
